/*-----------------------------------------------------------------------------/
/ EXC reviews, show a few at a time
/
/ Judge.me renders its whole page of reviews at once, which on desktop is one
/ full row plus a stranded second row, and on a phone is a very long scroll
/ before anything else on the page. This shows the first few and puts a button
/ under them that reveals the next few, so the section starts short and the
/ shopper opens it if they want it.
/
/ STEP is the only number to change: it is both how many show to begin with and
/ how many each press of the button adds. The widget is a Judge.me app block,
/ so there is no theme editor setting to hang this on.
/
/ Judge.me is a Vue app and rebuilds its list on paging, sorting and filtering,
/ so the list is watched and the count resets to STEP whenever it rebuilds.
/ If this script never runs, every review shows, which is Judge.me's own
/ behaviour.
/
/ Notes: hq/projects/excdesigns/theme-build-notes.md
/-----------------------------------------------------------------------------*/

const STEP = 3;
const LIST = '#judgeme_product_reviews .jdgm-review-list';
const HIDDEN = 'exc-review--hidden';

let shown = STEP;
let button = null;

function reviewRows(list) {
  return [...list.children];
}

function render(list) {
  const rows = reviewRows(list);

  rows.forEach((row, i) => {
    row.classList.toggle(HIDDEN, i >= shown);
  });

  const remaining = rows.length - shown;

  if (remaining <= 0) {
    if (button) button.hidden = true;
    return;
  }

  if (!button) {
    button = document.createElement('button');
    button.type = 'button';
    button.className = 'exc-reviews-more';
    button.addEventListener('click', () => {
      shown += STEP;
      render(list);
      // Keep the newly revealed reviews in view rather than jumping the page
      button.scrollIntoView({ block: 'nearest' });
    });
  }

  button.hidden = false;
  button.textContent =
    remaining === 1 ? 'Show 1 more review' : `Show ${Math.min(STEP, remaining)} more reviews`;

  if (button.previousElementSibling !== list) {
    list.insertAdjacentElement('afterend', button);
  }
}

// A mutation caused by us inserting or moving our own button is not a rebuild
function isOurs(records) {
  return records.every((record) =>
    [...record.addedNodes, ...record.removedNodes].every((node) => node === button)
  );
}

function watch(list) {
  render(list);

  // Judge.me replaces the rows when the shopper pages, sorts or filters, and
  // sometimes replaces the list element itself. Either way, go back to showing
  // the first few so a new page does not arrive already expanded.
  const rebuilt = new MutationObserver((records) => {
    if (isOurs(records)) return;
    shown = STEP;
    render(list);
  });
  rebuilt.observe(list, { childList: true });

  const replaced = new MutationObserver((records) => {
    if (isOurs(records)) return;
    const current = document.querySelector(LIST);
    if (current && current !== list) {
      rebuilt.disconnect();
      replaced.disconnect();
      shown = STEP;
      watch(current);
    }
  });
  if (list.parentElement) replaced.observe(list.parentElement, { childList: true });
}

function init() {
  const existing = document.querySelector(LIST);
  if (existing) {
    watch(existing);
    return;
  }

  // The widget mounts after the page does, so wait for the list to appear
  const waiting = new MutationObserver(() => {
    const list = document.querySelector(LIST);
    if (list) {
      waiting.disconnect();
      watch(list);
    }
  });
  waiting.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
